// @ts-strict-ignore
import { Typography } from '@mui/material';
import { useLocalStorage } from 'hooks/UseLocalStorage';
import { POSITION_DISPLAY } from 'phoenix/constants';
import { useAssetClassMetaV2 } from 'phoenix/models/AssetClasses/useAssetClass';
import { ApiPosition, OptionSymbol } from 'phoenix/redux/models';
import { useSecurityMetadataV2 } from 'phoenix/stores/SecurityMetadataV2Store';
import { ChangeColor, FormatNumber } from 'phoenix/util';
import { GetGainLossFiguresV2 } from 'phoenix/util/HoldingsCalculations';
import { XS } from 'phoenix/xstream/XS';
import React, { useMemo } from 'react';
import { DirectionalArrow, Flex } from '../..';
import style from '../style.module.scss';

interface PositionValueProps {
    chartPercChange: number;
    chartPrice: number;
    position: ApiPosition;
    symbol: string;
    withQuoteOverride?: boolean;
}

export const PositionValue = React.memo((props: PositionValueProps) => {
    const [display] = useLocalStorage(POSITION_DISPLAY, 'price');
    const { symbol } = props;
    const standardQuote = XS.Quotes.use(props.symbol);
    const optionsQuote = XS.OptionQuotes.use(props.symbol);
    const isOption = OptionSymbol.IsOptionSymbol(props.symbol);
    const assetClass = useAssetClassMetaV2(props.symbol);
    const quote = isOption ? optionsQuote : standardQuote;
    const meta = useSecurityMetadataV2().getMetadataBySymbol(symbol);
    const positionValues = useMemo(() => GetGainLossFiguresV2(props.position, quote), [JSON.stringify(props.position), JSON.stringify(quote)]);
    const price = useMemo(
        () => (display === 'value' ? positionValues.marketValue : quote?.price || props.chartPrice || positionValues.price),
        [JSON.stringify(positionValues), props.chartPrice, JSON.stringify(quote), display]
    );

    const { formatPrice } = useAssetClassMetaV2(symbol, { fetchMetadata: true });
    const priceFormatted = formatPrice(price, meta);
    const percentDigits = 2;

    const percChange = useMemo(() => {
        switch (true) {
            case props.withQuoteOverride && !isNaN(quote?.changePercent):
                return quote.changePercent * 100;
            case props.chartPercChange !== null && !isNaN(props.chartPercChange):
                return props.chartPercChange;
            case positionValues.changePercent !== null && !isNaN(positionValues.changePercent):
                return positionValues.changePercent / 100;
            case props.position.fallbackChangePercent || props.position.fallbackChangePercent === 0:
                return props.position.fallbackChangePercent * 100;
            default:
                return null;
        }
    }, [props.position.fallbackChangePercent, props.chartPercChange, positionValues.changePercent, JSON.stringify(quote), assetClass]);

    return (
        <Flex column className={style.pricingColumn}>
            <Typography variant='h6'>{priceFormatted}</Typography>
            {!isNaN(percChange) ? (
                <Flex align='center' justify='flex-end'>
                    <DirectionalArrow height={8} valueChange={percChange} />
                    <Typography style={{ paddingLeft: '5px', color: ChangeColor(percChange) }} variant='body1'>
                        {FormatNumber.toPercent(percChange, 'never', 2)}
                    </Typography>
                </Flex>
            ) : (
                <Typography className={ChangeColor(0)} style={{ paddingLeft: '5px' }} variant='body1'>
                    {FormatNumber.toPercent(0, 'never')}
                </Typography>
            )}
        </Flex>
    );
});
